import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { FadeInView } from "../../ui/fade-in";
import "./ambResponsibilities.css";

import marketingImg from "../../../assets/responsibility-marketing.webp";
import eventsImg from "../../../assets/responsibility-events.webp";
import feedbackImg from "../../../assets/responsibility-feedback.webp";
import growthImg from "../../../assets/responsibility-growth.webp";

const items = [
  {
    title: "Lead marketing campaigns",
    body: "Design the social content, pitch the ideas, and launch campaigns across campus, with the budget and the sign-off to run them.",
    image: marketingImg,
    alt: "An ambassador working on a campus campaign",
  },
  {
    title: "Get students onto RAIN",
    body: "Sign-ups are the point, and how you get them is up to you: tabling and info sessions if those work, just as often a class shout-out, a group chat, or word of mouth. We supply the materials.",
    image: eventsImg,
    alt: "Students at a campus event table",
  },
  {
    title: "Test the product and report back",
    body: "Try new features before they ship, survey students, and bring what you hear straight to the team. Ambassador feedback is where a lot of the roadmap starts.",
    image: feedbackImg,
    alt: "Students talking through feedback on campus",
  },
  {
    title: "Drive growth strategy",
    body: "Find new ways to get more students on and deepen the partnership with your career center, the part that looks best on a resume.",
    image: growthImg,
    alt: "Ambassadors planning growth strategy",
  },
];

/* How long each panel holds before the next one opens. */
const DWELL_MS = 12000;

const AmbResponsibilities = () => {
  const [active, setActive] = useState(0);
  const barRef = useRef(null);
  const pausedRef = useRef(false);
  const elapsedRef = useRef(0);

  /* One rAF loop drives the bar by writing to the node directly — putting the
     progress in state would re-render the whole section every frame. Only the
     panel change goes through React. */
  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;

    let raf;
    let last = performance.now();

    const tick = (now) => {
      const dt = now - last;
      last = now;

      if (!pausedRef.current) {
        elapsedRef.current += dt;
        if (elapsedRef.current >= DWELL_MS) {
          elapsedRef.current = 0;
          setActive((i) => (i + 1) % items.length);
        }
      }

      if (barRef.current) {
        barRef.current.style.transform = `scaleX(${elapsedRef.current / DWELL_MS})`;
      }
      raf = requestAnimationFrame(tick);
    };

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, []);

  const open = (i) => {
    elapsedRef.current = 0;
    setActive(i);
  };

  return (
    <section className="amb-section amb-resp">
      <div className="home-container">
        {/* Held until the section is on screen rather than fired on mount:
            it sits under the hero, so a mount animation has finished before
            anyone has scrolled to it. Same rise the headers use. */}
        <div className="amb-resp-head">
          <FadeInView delay={0}>
            <h2 className="amb-resp-title">What you&rsquo;d be doing</h2>
          </FadeInView>

          <FadeInView delay={0.1}>
            <p className="amb-resp-sub">
              We give you the tools, the budget, and the access. What you
              build with them is yours.
            </p>
          </FadeInView>
        </div>

        <div className="amb-resp-grid">
          {/* Every shot is mounted and only the active one is lit — swapping
              opacity beats remounting an <img decoding="async">, which flashes an empty frame
              while the next file decodes. */}
          <div className="amb-resp-stage">
            {items.map((item, i) => (
              <img decoding="async"
                key={item.title}
                src={item.image}
                alt={i === active ? item.alt : ""}
                className={`amb-resp-shot${i === active ? " amb-resp-shot-on" : ""}`}
                aria-hidden={i !== active}
              />
            ))}
          </div>

          {/* Only the accordion holds the timer — hovering the picture is not
              an attempt to read, so it should not stop the rotation. */}
          <ul
            className="amb-resp-list"
            onMouseEnter={() => { pausedRef.current = true; }}
            onMouseLeave={() => { pausedRef.current = false; }}
          >
            {items.map((item, i) => {
              const isOpen = i === active;
              return (
                <li
                  className={`amb-resp-item${isOpen ? " amb-resp-item-open" : ""}`}
                  key={item.title}
                >
                  <span className="amb-resp-rule" aria-hidden="true">
                    {isOpen && <span className="amb-resp-fill" ref={barRef} />}
                  </span>

                  <button
                    type="button"
                    className="amb-resp-q"
                    aria-expanded={isOpen}
                    onClick={() => open(i)}
                  >
                    <span>{item.title}</span>
                    <ChevronDown size={18} strokeWidth={2} className="amb-resp-chev" />
                  </button>

                  {/* Mounted at all times so the open and close can animate. */}
                  <div className="amb-resp-body" aria-hidden={!isOpen}>
                    <div className="amb-resp-body-inner">
                      <p className="amb-resp-text">{item.body}</p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </section>
  );
};

export default AmbResponsibilities;
